import { useState } from 'react';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EllipsisTitle from 'components/editor/SidePanel/DocumentSettings/CurrencyAutocomplete/EllipsisMenuItem';
import type { SxProps, Theme } from '@mui/material/styles';

export type BreadcrumbItem = {
  id: string;
  label: string;
};

export type BreadcrumbsProps = {
  items: BreadcrumbItem[];
  onSelectPath: (id: string) => void;
  sx?: SxProps<Theme>;
};

function MiddleCrumbs({ items, onSelect }: { items: BreadcrumbItem[]; onSelect: (id: string) => void }) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const isMenuOpen = Boolean(menuAnchor);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(event.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);
  const handleNavigate = (id: string) => {
    onSelect(id);
    handleMenuClose();
  };

  return (
    <>
      <IconButton
        size="small"
        color="primary"
        data-testid="middle-crumbs-menu-button"
        onClick={handleMenuOpen}
        sx={{ padding: '0 5px', backgroundColor: '#F5F5F5', borderRadius: '2px' }}
      >
        <MoreHorizIcon sx={{ width: '16px', height: '16px' }} />
      </IconButton>

      <Menu
        anchorEl={menuAnchor}
        open={isMenuOpen}
        onClose={handleMenuClose}
        aria-labelledby="breadcrumbs-menu"
        slotProps={{
          root: {
            sx: {
              '& .MuiMenuItem-root': (theme) => {
                return {
                  ...theme.typography.body2,
                  maxWidth: '200px',
                };
              },
            },
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {items.map((crumb) => (
          <MenuItem key={crumb.id} onClick={() => handleNavigate(crumb.id)}>
            <EllipsisTitle text={crumb.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function Breadcrumbs({ items, onSelectPath, sx }: BreadcrumbsProps) {
  const hasManyCrumbs = items.length > 2;
  const startCrumbs = hasManyCrumbs ? items.slice(0, 1) : items.slice(0, -1);
  const middleCrumbs = hasManyCrumbs ? items.slice(1, -1) : [];
  const lastCrumb = items.at(-1);

  return (
    <MuiBreadcrumbs
      aria-label="breadcrumbs"
      sx={{
        overflow: 'hidden',
        width: '100%',
        '& .MuiBreadcrumbs-ol': {
          flexWrap: 'nowrap',
          alignItems: 'center',
        },
        '& .MuiBreadcrumbs-li .MuiTypography-root': (theme) => {
          return {
            ...theme.typography.body2,
          };
        },
        '& .MuiBreadcrumbs-li:last-child': {
          overflow: 'hidden',
        },
        ...sx,
      }}
    >
      {startCrumbs.map((crumb) => (
        <Link key={crumb.id} onClick={() => onSelectPath(crumb.id)}>
          {crumb.label}
        </Link>
      ))}

      {middleCrumbs.length > 0 && <MiddleCrumbs key="middleCrumbs" items={middleCrumbs} onSelect={onSelectPath} />}

      {lastCrumb && <EllipsisTitle text={lastCrumb.label} />}
    </MuiBreadcrumbs>
  );
}
